/**
 * ScanReceiptDialog — "capture → OCR → confirm → save" in one place.
 *
 * Flow:
 *   1. Mounted with a file (image or PDF). Shows a spinner while we
 *      POST it to /api/receipts/parse.
 *   2. Once parsed, the dialog flips into edit mode: amount, merchant,
 *      date, category, account, type — all pre-filled from the OCR
 *      result but editable. Refunds are auto-coerced (so an incoming
 *      cashback never sneaks into Income).
 *   3. On Save → POST /api/transactions, fire `transactions:changed`
 *      event so other open pages can re-fetch, toast success with a
 *      "View" deep-link.
 *
 * The dialog is intentionally lean — no inline category creation, no
 * splits, no notes. Power editing happens on /transactions.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Loader2, Sparkles, Receipt, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';
import {
  autoCategorize, categoriesForType, isRefundDescription,
} from '@/lib/categorize';
import { CategorySelect } from '@/components/CategorySelect';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function freshToken() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch { return null; }
}

export default function ScanReceiptDialog({ file, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('parsing');   // parsing | review | saving
  const [parsed, setParsed] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    amount: '',
    type: 'expense',
    category: '',
    description: '',
    txn_date: new Date().toISOString().slice(0, 10),
    account_id: '',
    needs_review: false,
  });

  // -- Step 1: load user's accounts in the background. ----------------
  useEffect(() => {
    if (!user?.id) return;
    let cancel = false;
    supabase.from('accounts').select('id, name, type, last4').order('name').then((res) => {
      if (!cancel) setAccounts(res?.data || []);
    });
    return () => { cancel = true; };
  }, [user?.id]);

  // -- Step 2: as soon as we have a file, send to OCR. ----------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await freshToken();
        if (!token) throw new Error('Sign in to scan receipts.');

        // Read file → base64 (strip the data: prefix).
        const dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        const base64 = String(dataUrl).split(',')[1];

        const res = await axios.post(
          `${BACKEND_URL}/api/receipts/parse`,
          { image_base64: base64, mime_type: file.type || 'image/jpeg' },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 90000 },
        );
        if (cancelled) return;
        const r = res.data || {};
        // Refund coercion (same belt-and-suspenders rule as Transactions.js).
        let guessedType = r.type || 'expense';
        const refundyText = `${r.merchant || ''} ${r.note_to_user || ''}`;
        if (isRefundDescription(refundyText) || (guessedType === 'income' && isRefundDescription(r.merchant || ''))) {
          guessedType = 'refund';
        }
        const cats = categoriesForType(guessedType);
        const cat = cats.includes(r.category) ? r.category : autoCategorize(r.merchant || '', guessedType);

        // Pre-select account: prefer user's Cash account (most receipts are
        // cash), else the first account we have.
        const cashAcct = (accounts || []).find((a) => a.type === 'cash');
        const defaultAcct = cashAcct?.id || accounts[0]?.id || '';

        setParsed(r);
        setForm({
          amount: r.amount ? String(r.amount) : '',
          type: guessedType,
          category: cat || '',
          description: r.merchant || '',
          txn_date: r.date || new Date().toISOString().slice(0, 10),
          account_id: defaultAcct,
          needs_review: !!r.needs_review,
        });
        setPhase('review');
        if (r.needs_review) {
          toast.warning(r.note_to_user || 'Some details were hard to read — please review before saving.');
        }
      } catch (e) {
        if (cancelled) return;
        toast.error(e?.response?.data?.detail || e?.message || 'Could not read that receipt. Try a clearer image.');
        onClose?.();
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const cats = useMemo(() => categoriesForType(form.type), [form.type]);

  const save = async () => {
    if (!form.amount || Number.isNaN(Number(form.amount))) {
      toast.error('Enter an amount before saving.');
      return;
    }
    if (!form.description) {
      toast.error('Add a merchant or description.');
      return;
    }
    setPhase('saving');
    try {
      const amt = Math.abs(Number(form.amount));
      const signedAmount = form.type === 'income' || form.type === 'refund' ? amt : -amt;
      const row = {
        amount: signedAmount,
        type: form.type,
        category: form.category || null,
        description: form.description,
        txn_date: form.txn_date,
        account_id: form.account_id || null,
        user_id: user.id,
      };
      const { data, error } = await supabase.from('transactions').insert([row]).select('id').single();
      if (error) throw error;
      toast.success('Receipt saved.', {
        action: {
          label: 'View',
          onClick: () => navigate('/transactions'),
        },
      });
      // Broadcast so any open page can refresh without a hard reload.
      try { window.dispatchEvent(new CustomEvent('transactions:changed', { detail: { id: data?.id } })); } catch { /* swallow */ }
      onClose?.();
    } catch (e) {
      toast.error(e?.message || 'Could not save. Try again.');
      setPhase('review');
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid="scan-receipt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-primary" />
            {phase === 'parsing' ? 'Reading receipt…' : 'Confirm receipt'}
          </DialogTitle>
        </DialogHeader>

        {phase === 'parsing' ? (
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <Sparkles className="h-4 w-4 text-primary" />
              Extracting amount, merchant, and date…
            </div>
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-2/3" />
          </div>
        ) : (
          <div className="space-y-3 py-1" data-testid="scan-receipt-form">
            {form.needs_review && (
              <div className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/5 p-2.5 text-xs">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <span className="text-foreground/80">
                  {parsed?.note_to_user || 'Some details were hard to read. Please double-check before saving.'}
                </span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="sr-amount" className="text-xs">Amount</Label>
                <Input
                  id="sr-amount"
                  type="number"
                  inputMode="decimal"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="mt-1"
                  data-testid="scan-receipt-amount"
                />
              </div>
              <div>
                <Label htmlFor="sr-date" className="text-xs">Date</Label>
                <Input
                  id="sr-date"
                  type="date"
                  value={form.txn_date}
                  onChange={(e) => setForm({ ...form, txn_date: e.target.value })}
                  className="mt-1"
                  data-testid="scan-receipt-date"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="sr-desc" className="text-xs">Merchant / Description</Label>
              <Input
                id="sr-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1"
                placeholder="e.g. Starbucks"
                data-testid="scan-receipt-desc"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="sr-type" className="text-xs">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, category: '' })}>
                  <SelectTrigger id="sr-type" className="mt-1" data-testid="scan-receipt-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="refund">Refund</SelectItem>
                    <SelectItem value="investment">Investment</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="sr-cat" className="text-xs">Category</Label>
                <CategorySelect
                  id="sr-cat"
                  type={form.type}
                  value={form.category}
                  onChange={(v) => setForm({ ...form, category: v })}
                  placeholder="Pick one"
                  triggerClassName="mt-1"
                  contentClassName="max-h-64"
                  testId="scan-receipt-category"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="sr-acct" className="text-xs">Account</Label>
              <Select value={form.account_id || ''} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                <SelectTrigger id="sr-acct" className="mt-1" data-testid="scan-receipt-account">
                  <SelectValue placeholder={accounts.length ? 'Pick an account' : 'No accounts yet'} />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}{a.last4 ? ` ····${a.last4}` : ''}{a.type === 'cash' ? ' · Cash' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {parsed?.merchant && (
              <div className="flex items-center gap-1.5 pt-1">
                <Badge variant="outline" className="rounded-full text-[10px]">
                  <Sparkles className="h-2.5 w-2.5 mr-1" />Auto-filled by AI
                </Badge>
                <span className="text-[10px] text-muted-foreground">Edit anything before saving.</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={onClose} disabled={phase === 'saving'} data-testid="scan-receipt-cancel">
            Discard
          </Button>
          <Button onClick={save} disabled={phase !== 'review'} data-testid="scan-receipt-save">
            {phase === 'saving' ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving…</> : 'Save transaction'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
